import { Assert } from "../../Assert";
import { FomanticShim, Nullable, Nullish, PartialNullable } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyObject, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { IContainerControl } from "./ContainerControl";
import { IInputListControl, InputListControl, InputListControlPropertyValues } from "./InputListControl";
import { NAMESPACE } from "./_Namespace";

import "./ComboBox.scss";

export type ComboBoxPropertyValues =
    InputListControlPropertyValues
    & {
        allowEmptySelection: boolean;
        dropDownContextSelector: string;
        emptySelectionText: string;
        placeholder: string;
        searchable: boolean;
        selectedItem: any;
    };

export type ComboBoxProperties = {
    [name in keyof ComboBoxPropertyValues]: Property<ComboBoxPropertyValues[name]>
};

export interface IComboBox extends IInputListControl, ComboBoxProperties
{
}

export class ComboBox extends InputListControl implements IComboBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ComboBox";
    public static override className = "ComboBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ComboBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                allowEmptySelection: true,
                searchable: false,
                tabStop: true,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$iconElement?: JNative<HTMLElement>;
    protected $$inputElement?: JNative<HTMLElement>;
    protected $$menuElement?: JNative<HTMLElement>;
    protected $$placeHolderElement?: JNative<HTMLElement>;
    protected __dropDownInitialized?: boolean
    protected emptySelectionItem?: any;
    protected focusMethodCalled?: boolean;

    // #endregion

    // #region Properties

    public allowEmptySelection!: PropertyBoolean;
    public dropDownContextSelector!: PropertyString;
    public emptySelectionText!: PropertyString;
    public placeholder!: PropertyString;
    public searchable!: PropertyBoolean;
    public selectedItem!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ComboBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-combo-box ui selection dropdown");

        // Trigger Property Changes
        this.placeholder.triggerChange();
        this.searchable.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _allowEmptySelectionChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _createMenuItemElements(listData: any[], listDataSourceValueProperty: string, listDataSourceDescriptionProperty: string)
    {
        return (
            ArrayHelper.select(listData, item => JNativeStatic.create({
                nodeName: "div",
                attributes: {
                    "class": "item",
                    "data-value": StringHelper.htmlEncode(item[listDataSourceValueProperty])
                },
                text: item[listDataSourceDescriptionProperty],
                data: { "rad-option": item }
            }))
        );
    }
    protected _dropDownContextSelectorChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        const
            option = this._getSelectedOption(),
            listDataSourceValueProperty = this.listDataSourceValueProperty.get();

        if (!option || !listDataSourceValueProperty || ObjectHelper.isNullOrUndefined(option[listDataSourceValueProperty]) || option[listDataSourceValueProperty] === StringHelper.empty)
            return null;

        return option[listDataSourceValueProperty];
    }
    protected _elementValueSet(value: any)
    {
        this.__processElementValueChanged = false;

        FomanticShim(this.$$element.get()).dropdown("set selected", !ObjectHelper.isNullOrUndefined(value) ? value.toString() : StringHelper.empty);

        if (ObjectHelper.isNullOrUndefined(value) || value === StringHelper.empty)
        {
            Assert.isNotNullOrUndefined(this.$$placeHolderElement, "this.$$placeHolderElement");

            this.$$placeHolderElement.text(this.placeholder.get());
            this.$$placeHolderElement.addClass("default");
        }

        this.__processElementValueChanged = true;
    }
    protected _emptySelectionTextChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _getSelectedOption()
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");
        Assert.isNotNullOrUndefined(this.$$menuElement, "this.$$menuElement");

        const
            value = FomanticShim($$element).dropdown("get value"),
            $$item = this.$$menuElement.find(StringHelper.format(">div[data-value=\"{0}\"]", value));

        if (!$$item || !$$item.length)
            return null;

        const
            option = $$item.dataGet("rad-option");

        return option ? option : null;
    }
    protected _placeholderChanged(change: PropertyChange<string>)
    {
        if (!ObjectHelper.isNullOrUndefined(this.value.get()))
            return;

        Assert.isNotNullOrUndefined(this.$$placeHolderElement, "this.$$placeHolderElement");

        this.$$placeHolderElement.text(change.currentValue);
        this.$$placeHolderElement.addClass("default");

        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        // this line tells semantic that this is the default state (for use later with "restore defaults")
        FomanticShim($$element).dropdown("save defaults");
    }
    protected _searchableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("search");
        else
            this.removeCssClass("search");
    }
    protected _selectedItemChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ComboBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.allowEmptySelection = new PropertyBoolean(this, propertyValues.allowEmptySelection, this._allowEmptySelectionChanged);
        this.dropDownContextSelector = new PropertyString(this, propertyValues.dropDownContextSelector, this._dropDownContextSelectorChanged);
        this.emptySelectionText = new PropertyString(this, propertyValues.emptySelectionText, this._emptySelectionTextChanged);
        this.placeholder = new PropertyString({
            context: this,
            initialValue: propertyValues.placeholder,
            changeHandler: this._placeholderChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Placeholder" }),
            ],
        });
        this.searchable = new PropertyBoolean(this, propertyValues.searchable, this._searchableChanged);
        this.selectedItem = new PropertyObject(this, propertyValues.selectedItem, this._selectedItemChanged);

        this.text.overrideGet(next =>
        {
            const
                selectedItem = this.selectedItem.get(),
                listDataSourceDescriptionProperty = this.listDataSourceDescriptionProperty.get();

            if (!selectedItem || !listDataSourceDescriptionProperty)
                return null;

            return selectedItem[listDataSourceDescriptionProperty];
        });
        this.value.overrideSet((value, next) =>
        {
            value = value === undefined ? null : value;

            const
                listDataSource = this.listDataSource.get(),
                listDataSourceData: Nullable<any[]> = listDataSource ? listDataSource.data.get() : null,
                listDataSourceValueProperty = this.listDataSourceValueProperty.get();

            let
                selectedItem =
                    value !== null && listDataSourceData && listDataSourceValueProperty
                        ? ArrayHelper.firstOrDefault(listDataSourceData, item => item[listDataSourceValueProperty] === value)
                        : null;

            if (!selectedItem)
            {
                if (listDataSourceData?.length && listDataSourceValueProperty && !this.allowEmptySelection.get())
                {
                    selectedItem = ArrayHelper.first(listDataSourceData);
                    value = selectedItem[listDataSourceValueProperty];
                    this._setDataSourceValue(value);
                }
                else
                    value = null;
            }

            let
                elementValue = value;

            if (elementValue === null && this.allowEmptySelection.get())
                elementValue = StringHelper.empty;

            this.selectedItem.set(selectedItem);
            next(value);
            this._elementValueSet(elementValue);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();
        this.on("change", { self: this }, this.onChange);
        this.on("focus", { self: this }, this.onFocus);
        this.on("keyup", { self: this }, this.onKeyUp);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        Assert.isNotNullOrUndefined($$element, "$$element");

        this.$$inputElement =
            JNativeStatic.create$$({
                nodeName: "input",
                attributes: { type: "hidden" }
            }).appendTo($$element);

        this.$$iconElement = JNativeStatic.create$$("i", "dropdown icon").appendTo($$element);
        this.$$placeHolderElement = JNativeStatic.create$$("div", "default text").appendTo($$element);
        this.$$menuElement = JNativeStatic.create$$("div", "menu").appendTo($$element);

        return $$element;
    }
    protected override _refreshListDataSource()
    {
        super._refreshListDataSource();

        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get(),
            backupValue = dataSource && dataSourceProperty ? dataSource.propertyValueByPathGet(dataSourceProperty) : this.value.get(),
            listDataSource = this.listDataSource.get(),
            listData = listDataSource ? listDataSource.data.get() : null,
            listDataSourceValueProperty = this.listDataSourceValueProperty.get(),
            listDataSourceDescriptionProperty = this.listDataSourceDescriptionProperty.get();

        if (!listData || !listDataSourceValueProperty || !listDataSourceDescriptionProperty)
            return;

        Assert.isNotNullOrUndefined(this.$$menuElement, "self.$$menuElement");

        this.$$menuElement.empty();

        if (this.allowEmptySelection.get())
        {
            if (!this.emptySelectionItem)
                this.emptySelectionItem = {};

            this.emptySelectionItem[listDataSourceDescriptionProperty] = this.emptySelectionText.get() ?? StringHelper.empty;
            this.emptySelectionItem[listDataSourceValueProperty] = StringHelper.empty;

            this.$$menuElement.append(JNativeStatic.create({
                nodeName: "div",
                attributes: {
                    "class": "item rad-item-empty",
                    "data-value": StringHelper.htmlEncode(this.emptySelectionItem[listDataSourceValueProperty])
                },
                text: this.emptySelectionItem[listDataSourceDescriptionProperty],
                data: { "rad-option": this.emptySelectionItem }
            }));
        }

        this.$$menuElement.append(this._createMenuItemElements(listData, listDataSourceValueProperty, listDataSourceDescriptionProperty));

        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        FomanticShim($$element).dropdown("refresh");

        this.value.set(backupValue);
    }

    public override appendTo($$elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>, beforeSelector?: any)
    {
        const
            result = super.appendTo($$elementOrContainerControl, beforeSelector);

        if (!this.__dropDownInitialized)
        {
            const
                $$element = this.$$element.get();

            Assert.isNotNullOrUndefined($$element, "$$element");

            const
                dropDownContextSelector = this.dropDownContextSelector.get(),
                $$dropDownContext = dropDownContextSelector ? $$element.parent().closest(dropDownContextSelector) : null,
                dropDownContext = $$dropDownContext?.length ? $$dropDownContext[0] : window,
                searchable = this.searchable.get();

            FomanticShim($$element).dropdown({
                context: dropDownContext,
                fullTextSearch: searchable ? "exact" : false,
                ignoreDiacritics: true,
                match: "text",
                showOnFocus: false
            });

            Assert.isNotNullOrUndefined(this.$$placeHolderElement, "this.$$placeHolderElement");

            /* INFO: LEAVE THESE LINES IN
               Replace placeholder as it gets cleared when the dropdown initializes. */
            this.$$placeHolderElement.text(this.placeholder.get());
            this.$$placeHolderElement.addClass("default");

            // this line tells semantic that this is the default state (for use later with "restore defaults")
            FomanticShim($$element).dropdown("save defaults");

            this.__dropDownInitialized = true;
        }

        return result;
    }
    public override focus()
    {
        this.focusMethodCalled = true;
        super.focus();
        this.focusMethodCalled = false;
    }

    // #endregion

    // #region Event Handlers

    protected onChange(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }
    protected onClick(evt: IJNativeEvent)
    {
        evt.stopPropagation();
    }
    protected onFocus(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        let
            $$element = self.$$element.get();

        if (!self.focusMethodCalled)
            setTimeout(() => { FomanticShim($$element).dropdown("show"); $$element = null; }, 250);
    }
    protected onKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this,
            $$element = self.$$element.get();

        if (evt.originalEvent.key === "Escape" && FomanticShim($$element).dropdown("is visible"))
            evt.stopPropagation();
    }

    // #endregion

    // #endregion
}

